import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const capabilities = [
  {
    icon: 'ri-microscope-line',
    title: 'Análisis HPHT',
    desc: 'Simulamos condiciones de alta presión y temperatura que replican exactamente el ambiente del yacimiento.',
    stat: '200°C',
    statLabel: 'Temperatura máx.',
  },
  {
    icon: 'ri-test-tube-line',
    title: 'Control de Calidad',
    desc: 'Cada lote es analizado y certificado antes de salir de nuestras instalaciones con ficha técnica y MSDS.',
    stat: '100%',
    statLabel: 'Lotes certificados',
  },
  {
    icon: 'ri-flask-line',
    title: 'I+D de Fórmulas',
    desc: 'Desarrollamos y mejoramos fórmulas continuamente en respuesta a los desafíos reales del campo venezolano.',
    stat: '50+',
    statLabel: 'Fórmulas activas',
  },
  {
    icon: 'ri-drop-line',
    title: 'Reología de Fluidos',
    desc: 'Medición de viscosidad, gelificación y densidad en condiciones dinámicas para fluidos de perforación.',
    stat: '24h',
    statLabel: 'Respuesta técnica',
  },
];

const labImages = [
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/42862eddf739d32b013cdf187dde0b32.jpeg',
    label: 'Formulación de aditivos',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/5b41f56eb29c2520b2a33d683b226285.jpeg',
    label: 'Evaluación de muestras',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/82c727ebc95c127b734df257ae7b9094.jpeg',
    label: 'Baño calefactor de análisis',
  },
  {
    src: 'https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/4391104b0a5ec37ea08d931cfb801c88.jpeg',
    label: 'Tubos de separación crudo-agua',
  },
];

export default function ExpertiseBanner() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* ── EXPERTISE dark banner ── */}
      <section ref={ref} className="relative w-full py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Advanced%20petrochemical%20laboratory%20with%20glowing%20scientific%20equipment%2C%20test%20tubes%20and%20beakers%20filled%20with%20dark%20crude%20oil%20samples%2C%20precision%20analytical%20instruments%2C%20deep%20teal%20and%20amber%20lighting%2C%20dramatic%20science%20atmosphere%2C%20bokeh%20background%20with%20lab%20equipment%20silhouettes%2C%20professional%20chemistry%20research%20environment&width=1800&height=850&seq=expertise-lab-banner-01&orientation=landscape"
            alt="Laboratorio avanzado Poly Alphas"
            className="w-full h-full object-cover object-center"
          />
          {/* Double overlay for maximum darkness */}
          <div className="absolute inset-0 bg-foreground-950/75" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/92 to-foreground-950/65" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className={`max-w-3xl mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-px bg-primary-500" />
              <span className="text-primary-400 text-xs font-semibold tracking-widest uppercase">
                Capacidades Técnicas
              </span>
            </div>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white leading-tight mb-4">
              Un laboratorio diseñado{' '}
              <span className="text-primary-400">para ganar en campo</span>
            </h2>
            <p className="text-foreground-200 text-lg leading-relaxed">
              Nuestro laboratorio es el corazón de la operación. Aquí nacen las fórmulas,
              se validan los resultados y se garantiza que cada producto que sale de nuestras
              instalaciones está listo para las condiciones más exigentes.
            </p>
          </div>

          {/* Capabilities grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
            {capabilities.map((cap, i) => (
              <div
                key={cap.title}
                className={`p-6 rounded-2xl bg-white/10 border border-white/15 hover:border-primary-500/50 hover:bg-white/14 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 0.12 + 0.15}s` }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/25 mb-4">
                  <i className={`${cap.icon} text-primary-400 text-2xl`}></i>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{cap.title}</h3>
                <p className="text-foreground-300 text-sm leading-relaxed mb-4">{cap.desc}</p>
                <div className="pt-3 border-t border-white/15">
                  <span className="text-accent-400 font-bold text-2xl">{cap.stat}</span>
                  <span className="text-foreground-400 text-xs block mt-0.5">{cap.statLabel}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom: quote + CTA */}
          <div className={`flex flex-col lg:flex-row items-center gap-8 p-8 rounded-2xl bg-primary-500/20 border border-primary-500/40 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/30 flex-shrink-0 mt-1">
                  <i className="ri-double-quotes-l text-primary-400 text-lg"></i>
                </div>
                <div>
                  <p className="text-white text-lg font-semibold leading-snug mb-2">
                    "La diferencia entre un químico genérico y el nuestro está en el laboratorio."
                  </p>
                  <p className="text-foreground-300 text-sm">
                    Cada fórmula es adaptada a las condiciones específicas del yacimiento venezolano —
                    temperatura, salinidad, composición de crudo y presión de formación.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Link
                to="/catalogo"
                className="inline-flex items-center gap-2 px-7 py-4 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flask-line"></i>
                Explorar Catálogo Técnico
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── LAB PHOTOS mosaic ── */}
      <section className="w-full py-0">
        <div className="relative">
          <div className="grid grid-cols-2 md:grid-cols-4 h-[340px] md:h-[420px]">
            {labImages.map((img) => (
              <div key={img.label} className="relative overflow-hidden group">
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent" />
                <div className="absolute inset-0 bg-foreground-950/20 group-hover:bg-foreground-950/0 transition-colors duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="text-white text-xs font-semibold bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
                    <i className="ri-flask-line mr-1.5"></i>{img.label}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Overlay badge */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
            <div className="px-6 py-3 rounded-full bg-foreground-950/80 border border-white/25 backdrop-blur-md">
              <span className="text-white text-sm font-semibold tracking-wide flex items-center gap-2">
                <i className="ri-camera-lens-line text-accent-400"></i>
                Laboratorio — Instalaciones Reales
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── TEAM banner — foto real del equipo en campo ── */}
      <section className="relative w-full py-24 md:py-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          {/* Foto real: ingenieros en campo petrolero Venezuela */}
          <img
            src="https://static.readdy.ai/image/51f676c9cc2d72a77723e075d004b9cd/0f1d890d7f75a794dff045c12eca6044.jpeg"
            alt="Ingenieros Poly Alphas en campo petrolero Venezuela"
            className="w-full h-full object-cover object-center"
          />
          {/* Overlay doble para máximo contraste sobre foto clara */}
          <div className="absolute inset-0 bg-foreground-950/60" />
          <div className="absolute inset-0 bg-gradient-to-l from-foreground-950 via-foreground-950/92 to-foreground-950/45" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex justify-end">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-px bg-accent-500" />
                <span className="text-accent-400 text-xs font-semibold tracking-widest uppercase">
                  Nuestro Equipo
                </span>
              </div>

              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white leading-tight mb-6">
                Ingenieros que{' '}
                <span className="text-accent-400">van al campo</span>{' '}
                contigo
              </h2>

              <p className="text-foreground-200 text-lg leading-relaxed mb-8">
                No solo entregamos el producto. Nuestro equipo de ingenieros químicos se desplaza
                directamente a las instalaciones para supervisar la aplicación, ajustar dosis
                y garantizar resultados medibles.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: 'ri-graduation-cap-line', text: 'Ingenieros químicos certificados' },
                  { icon: 'ri-map-pin-line', text: 'Presencia en todo el país' },
                  { icon: 'ri-timer-line', text: 'Respuesta en menos de 24h' },
                  { icon: 'ri-phone-line', text: 'Soporte técnico continuo' },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-3 p-4 rounded-xl bg-white/10 border border-white/18">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/25 flex-shrink-0">
                      <i className={`${item.icon} text-accent-400 text-sm`}></i>
                    </div>
                    <span className="text-foreground-200 text-sm leading-snug">{item.text}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/servicios"
                className="inline-flex items-center gap-2 px-7 py-4 bg-accent-500 hover:bg-accent-400 text-white font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-tools-line"></i>
                Ver Nuestros Servicios
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}